import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Membership, Organization } from "@lawnops/shared";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthProvider";

interface OrgContextValue {
  loading: boolean;
  memberships: Array<Membership & { organization: Organization }>;
  activeOrgId: string | null;
  activeOrg: Organization | null;
  activeMembership: Membership | null;
  setActiveOrgId: (id: string) => void;
  refresh: () => Promise<void>;
}

const OrgContext = createContext<OrgContextValue | undefined>(undefined);
const ACTIVE_ORG_KEY = "lawnops:activeOrgId";

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<OrgContextValue["memberships"]>([]);
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_ORG_KEY),
  );
  const [loading, setLoading] = useState<boolean>(Boolean(user));

  const load = useCallback(async () => {
    if (!user || !supabase) {
      setMemberships([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("memberships")
      .select("*, organization:organizations(*)")
      .eq("user_id", user.id);
    if (!error && data) {
      setMemberships(data as OrgContextValue["memberships"]);
      if (data.length > 0) {
        const ids = data.map((m) => m.organization_id as string);
        const stored = localStorage.getItem(ACTIVE_ORG_KEY);
        if (!stored || !ids.includes(stored)) {
          const first = ids[0]!;
          setActiveOrgIdState(first);
          localStorage.setItem(ACTIVE_ORG_KEY, first);
        }
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const setActiveOrgId = useCallback((id: string) => {
    setActiveOrgIdState(id);
    localStorage.setItem(ACTIVE_ORG_KEY, id);
  }, []);

  const value = useMemo<OrgContextValue>(() => {
    const active = memberships.find((m) => m.organization_id === activeOrgId) ?? null;
    return {
      loading,
      memberships,
      activeOrgId,
      activeOrg: active?.organization ?? null,
      activeMembership: active
        ? ({
            id: active.id,
            organization_id: active.organization_id,
            user_id: active.user_id,
            role: active.role,
            created_at: active.created_at,
            updated_at: active.updated_at,
          } as Membership)
        : null,
      setActiveOrgId,
      refresh: load,
    };
  }, [memberships, activeOrgId, loading, setActiveOrgId, load]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be used inside <OrgProvider>");
  return ctx;
}
